import type {
  FieldConfigDto,
  FormVersionDto,
  SubformDto,
  TriggerDto,
  VariableConfigDto,
} from "../../services/ApiService";
import type { FormVersion, Rule, Subform, SubformEmailConfiguration } from "../../state/formVersion";
import type { Form } from "../../state/form";
import { isArray, isEmpty, isNil, isString, keyBy } from "lodash-es";
import { toField, widgetUidToField } from "../../fields/fields";
import type {
  ArraySchema,
  BaseIssue,
  GenericSchema,
  IntersectOptions,
  IntersectSchema,
  ObjectEntries,
  ObjectSchema,
  UnionOptions,
  UnionSchema,
} from "valibot";
import { safeParse } from "valibot";
import FieldId from "../../fields/FieldId";
import SubformId from "../../state/SubformId";
import { toIntegration, toIntegrationDtos, toTriggerDtos, triggerToIntegration } from "./integration";
import type {
  BaseField,
  FieldConfig,
  FieldLabelTextProperties,
  UnknownFieldConfig,
  VariableConfig,
} from "../../fields/types";
import { VariableState } from "../../fields/types";
import { toRule, toRuleDto } from "./rule";
import type { SubformFieldProperties } from "../../fields/subform/SubformField";
import type { PinFieldProperties } from "../../fields/media/PinField";
import logger from "../logger";
import { COMMIT_SHA } from "../../constants";
import { INTEGRATION_TYPES } from "../../integrations/types";
import FieldValueReference from "../../fields/FieldValueReference";
import { parseStringTemplate, stringTemplateToString } from "../stringTemplateUtil";
import VariableValueReference from "../../variables/VariableValueReference";
import { ValueReference } from "../../types/ValueReference";
import FormId from "../../state/FormId";
import FormVersionId from "../../state/FormVersionId";
import { Scope } from "../../types";
import toVariableDefinition from "../../variables/toVariableDefinition";
import VariableId from "../../variables/VariableId";
import { toSubformEmailConfiguration } from "./subformEmailConfiguration";

const isSubformField = (
  field: FieldConfig,
): field is FieldConfig<
  {
    form?: SubformDto;
    target_form_id?: SubformId;
  } & Required<FieldLabelTextProperties>
> => field.type === "subform";

const isPinField = (
  field: FieldConfig,
): field is FieldConfig<{
  pins: {
    form?: SubformDto;
    target_form_id?: SubformId;
    name: string;
  }[];
}> => field.type === "pin";

const isUnknownField = (field: FieldConfig): field is UnknownFieldConfig => field.type === "unknown";

export const toFieldConfigDto = (
  field: FieldConfig,
  allFields: FieldConfig[],
  variables: VariableConfig[],
): FieldConfigDto => {
  const fieldType = toField(field.type);
  const properties = fieldType.toDto(field.properties, allFields, variables) as Record<string, unknown>;
  const widget = isUnknownField(field) ? field.widget : fieldType.widget;

  return { widget, uid: field.uid.toString(), properties };
};

const toSubformTriggerDto = (
  configuration: SubformEmailConfiguration,
  subformFields: FieldConfig[],
  variables: VariableConfig[],
): TriggerDto => ({
  type: "mail",
  name: "Mail notification",
  content: stringTemplateToString(configuration.content, subformFields, variables, "${", "", true),
  staticRecipients: [],
  dynamicRecipients: [],
  subject: "",
  fromName: "",
  replyTo: "",
  pdfFilename: "",
  pdfContent: configuration.pdfContent,
  configuration: {
    attachments: configuration.pinFieldsToAttach
      .map((fieldId) => fieldIdToDataName(fieldId, subformFields))
      .filter(removeUndefined),
    attachImages: true,
    attachPdf: true,
    embedImages: true,
    simpleMode: configuration.simpleMode,
    useHeaderAndFooter: true,
    horizontalOrientation: configuration.horizontalOrientation,
    hideNoValues: configuration.hideNoValues,
    distributedGeneration: false,
    legacyGeneration: false,
  },
});

export const toFormVersionDto = (formVersion: FormVersion): FormVersionDto => {
  const rootFields = formVersion.fields.filter((field) => field.scope === Scope.ROOT);

  return {
    id: formVersion.id.toString(),
    formId: formVersion.formId.toString(),
    fields: rootFields.map((field) => toFieldConfigDto(field, formVersion.fields, formVersion.variables)),
    variables: formVersion.variables.map(toVariableConfigDto),
    triggers: toTriggerDtos(formVersion.integrations, rootFields, formVersion.variables),
    integrations: toIntegrationDtos(formVersion.integrations, rootFields, formVersion.variables),
    meta: {
      ...formVersion.meta,
      created: formVersion.meta.created.toISOString(),
      lastUpdated: formVersion.meta.lastUpdated.toISOString(),
      lastUpdatedWith: COMMIT_SHA,
    },
    settings: {
      saveMode: formVersion.settings.saveMode,
      itemHtml: stringTemplateToString(formVersion.settings.descriptionInApp, rootFields, formVersion.variables),
      searchSettings: {
        enabled: formVersion.settings.searchSettings.enabled,
        onlyForCurrentUser: !formVersion.settings.searchSettings.allowSearchingAllSubmissions,
        filteringEnabled: formVersion.settings.searchSettings.filteringEnabled,
        filteredFields: formVersion.settings.searchSettings.filteredFields.map((fieldId) => fieldId.toString()),
        fields: formVersion.settings.searchSettings.fields.reduce(
          (acc, fieldId) => ({ ...acc, [fieldId.toString()]: true }),
          {},
        ),
      },
    },
    rules: formVersion.rules
      .filter((rule) => rule.scope === Scope.ROOT)
      .map((rule) => toRuleDto(rule, formVersion.fields, formVersion.variables)),
    fieldProperties: keyBy(
      formVersion.subforms.map((subform) => toSubformDto(subform, formVersion)),
      "uid",
    ),
    theme: { id: formVersion.theme.id },
  };
};

export const toSubformDto = (subform: Subform, formVersion: FormVersion): SubformDto => {
  const fields = formVersion.fields.filter((field) => subform.uid.equals(field.scope));

  return {
    uid: subform.uid.toString(),
    meta: subform.meta,
    fields: fields.map((field) => toFieldConfigDto(field, formVersion.fields, formVersion.variables)),
    rules: formVersion.rules
      .filter((rule) => subform.uid.equals(rule.scope))
      .map((rule) => toRuleDto(rule, formVersion.fields, formVersion.variables)),
    triggers:
      subform.emailConfiguration !== undefined
        ? [toSubformTriggerDto(subform.emailConfiguration, fields, formVersion.variables)]
        : [],
  };
};

const toFieldConfig =
  (addMissingProperties: boolean, variables: VariableConfig[], form?: Form, subformId?: SubformId) =>
  (field: FieldConfigDto): FieldConfig => {
    const fieldType = widgetUidToField(field.widget);
    const properties = fieldType.fromDto(field.properties) as Record<string, unknown>;
    return {
      type: fieldType.type,
      ...(fieldType.type === "unknown" ? { widget: field.widget } : {}),
      uid: FieldId.from(field.uid),
      scope: subformId ?? Scope.ROOT,
      properties: {
        ...(addMissingProperties ? fieldType.defaultProperties() : {}),
        ...replaceIdsWithValueReferences(fieldType, properties, variables),
      },
      state: isNil(form)
        ? "unknown"
        : (form.publishedVersion?.formVersion?.fields?.some((remoteField) => remoteField.uid.equals(field.uid)) ??
            false)
          ? "published"
          : "unpublished",
    };
  };

export const toVariableConfig = (variable: VariableConfigDto, form?: Form): VariableConfig => {
  const definition = toVariableDefinition(variable.type);
  const domainConfig = safeParse(definition.schema, variable).output as VariableConfig;

  return {
    ...domainConfig,
    uid: VariableId.from(variable.uid),
    state: isNil(form)
      ? VariableState.UNKNOWN
      : (form.publishedVersion?.formVersion?.variables?.some((remoteVariable) =>
            remoteVariable.uid.equals(variable.uid),
          ) ?? false)
        ? VariableState.PUBLISHED
        : VariableState.UNPUBLISHED,
  };
};

const toVariableConfigDto = (variable: VariableConfig): VariableConfigDto => ({
  uid: variable.uid.toString(),
  type: variable.type,
  name: variable.name,
  dataName: variable.dataName,
  defaultValue: variable.defaultValue,
});

export const toFormVersion = (
  formVersionDto: FormVersionDto,
  addMissingProperties: boolean,
  form?: Form,
): FormVersion => {
  const variables = formVersionDto.variables?.map((variable) => toVariableConfig(variable, form)) ?? [];
  const variableMap = toVariableMap(variables);
  const allSubformFields: FieldConfig[] = [];
  const subformRules: Rule[] = [];
  const subforms: Subform[] = Object.values(formVersionDto.fieldProperties).map((subformDto) => {
    const subformId = SubformId.from(subformDto.uid);
    const subformFields = subformDto.fields.map(toFieldConfig(addMissingProperties, variables, form, subformId));
    allSubformFields.push(...subformFields);

    const subformFieldMap = toFieldMap(allSubformFields);
    subformRules.push(...subformDto.rules.map((ruleDto) => toRule(ruleDto, subformFieldMap, variableMap, subformId)));

    const metaDto = subformDto.meta ?? {};

    return {
      uid: subformId,
      meta: {
        name: metaDto.name ?? "",
      },
      emailConfiguration: toSubformEmailConfiguration(subformDto, subformFields, variables),
    };
  });
  const rootFields = formVersionDto.fields.map(toFieldConfig(addMissingProperties, variables, form));
  const rootFieldMap = toFieldMap(rootFields);

  const rootRules = formVersionDto.rules.map((ruleDto) => toRule(ruleDto, rootFieldMap, variableMap));

  // Convert all 'normal' subforms to linked subforms. Can be removed after all subforms have been converted to linked.
  // !! Be aware that the conversion mutates the fields in the form version.
  const { convertedSubforms, convertedFields, convertedRules } = convertToLinkedSubforms(
    rootFields,
    addMissingProperties,
    form,
  );

  const allFields = [...rootFields, ...allSubformFields, ...convertedFields];

  // In order to transform string values to `TemplatedValues` we need to have all fields. So after converting to domain
  // objects we have to do another round of processing field properties.
  allFields.forEach(
    (field) =>
      (field.properties = toField(field.type).postProcessFromDto(
        field.properties,
        allFields,
        variables,
        field.scope,
      ) as Record<string, unknown>),
  );

  removeCyclicReferences(allFields, [...subforms, ...convertedSubforms], formVersionDto.id);

  return {
    id: FormVersionId.from(formVersionDto.id),
    formId: FormId.from(formVersionDto.formId),
    meta: {
      created: new Date(formVersionDto.meta.created),
      createdBy: formVersionDto.meta.createdBy,
      lastUpdated: new Date(formVersionDto.meta.lastUpdated),
      lastUpdatedBy: formVersionDto.meta.lastUpdatedBy,
      status: formVersionDto.meta.status,
    },
    fields: allFields,
    variables: variables,
    subforms: [...subforms, ...convertedSubforms],
    rules: [...rootRules, ...subformRules, ...convertedRules],
    integrations: [
      ...(formVersionDto.triggers?.map((trigger) => triggerToIntegration(trigger, rootFields, variables)) ?? []),
      ...(formVersionDto.integrations
        ?.filter((integration) => INTEGRATION_TYPES.includes(integration.key))
        ?.map((integration) => toIntegration(integration, rootFields, variables)) ?? []),
    ],
    settings: {
      saveMode: formVersionDto.settings.saveMode,
      descriptionInApp: parseStringTemplate(formVersionDto.settings.itemHtml, rootFields, variables),
      searchSettings: {
        enabled: formVersionDto.settings.searchSettings.enabled ?? false,
        allowSearchingAllSubmissions: !(formVersionDto.settings.searchSettings.onlyForCurrentUser ?? false),
        filteringEnabled: formVersionDto.settings.searchSettings.filteringEnabled ?? false,
        filteredFields:
          formVersionDto.settings.searchSettings.filteredFields?.map((fieldId) => new FieldId(fieldId)) ?? [],
        fields: Object.entries(formVersionDto.settings.searchSettings.fields ?? {})
          .filter(([, value]) => value)
          .map(([key]) => FieldId.from(key)),
      },
    },
    theme: {
      id: formVersionDto.theme.id,
    },
  };
};

export const toFieldMap = (fields: FieldConfig[]) =>
  fields.reduce((acc, field) => {
    acc.set(field.uid.toString(), field);
    return acc;
  }, new Map<string, FieldConfig>());

export const toVariableMap = (variables: VariableConfig[]) =>
  variables.reduce((acc, variable) => {
    acc.set(variable.uid.toString(), variable);
    return acc;
  }, new Map<string, VariableConfig>());

const removeCyclicReferences = (fields: FieldConfig[], subforms: Subform[], formVersionId: string) => {
  const fieldsByScope = fields.reduce<Record<string, FieldConfig[]>>((acc, field) => {
    const scopeKey = field.scope.toString();
    acc[scopeKey] = acc[scopeKey] ?? [];
    acc[scopeKey].push(field);
    return acc;
  }, {});

  const isCyclic = (subformId: SubformId, visited = new Set<string>()): void => {
    const subformFields = fieldsByScope[subformId.toString()] ?? [];

    subformFields.forEach((field) => {
      if (field.type === "subform") {
        const subformProperties = field.properties as SubformFieldProperties;
        const targetFormId = subformProperties.target_form_id;

        if (!isNil(targetFormId)) {
          if (visited.has(targetFormId.toString())) {
            // @ts-expect-error intentionally set to null to break cyclic dependency
            subformProperties.target_form_id = null;
            logger.sendToLogCollector("FormVersion mapping found cyclic dependency on subform field", {
              formVersionId,
              targetFormId,
            });
          } else {
            // Mark as visited and recurse into nested subform fields
            visited.add(targetFormId.toString());
            isCyclic(targetFormId, visited);
            visited.delete(targetFormId.toString()); // Backtrack for further branches
          }
        }
      } else if (field.type === "pin") {
        // Process 'pin' type fields
        const pinProperties = field.properties as PinFieldProperties;
        const pinList = pinProperties.pins ?? []; // Assume 'pins' is a list of pin objects

        pinList.forEach((pin) => {
          const targetFormId = pin.target_form_id;

          if (!isNil(targetFormId)) {
            if (visited.has(targetFormId.toString())) {
              pin.target_form_id = null;
              logger.sendToLogCollector("FormVersion mapping found cyclic dependency on pin", { formVersionId, pin });
            } else {
              visited.add(targetFormId.toString());
              isCyclic(targetFormId, visited);
              visited.delete(targetFormId.toString()); // Backtrack for further branches
            }
          }
        });
      }
    });
  };

  // Iterate through all subforms and check for cyclic dependencies
  subforms.forEach((subform) => {
    isCyclic(subform.uid);
  });
};

const convertToLinkedSubforms = (
  fields: FieldConfig[],
  addMissingProperties: boolean,
  form?: Form,
): { convertedSubforms: Subform[]; convertedFields: FieldConfig[]; convertedRules: Rule[] } => {
  const convertedSubforms: Subform[] = [];
  const convertedFields: FieldConfig[] = [];
  const convertedRules: Rule[] = [];

  fields.forEach((field) => {
    if (isSubformField(field)) {
      // If both 'target_form_id' and 'form' are present, then 'target_form_id' is preferred and data in 'form' is
      // ditched without conversion.
      if (isEmpty(field.properties.target_form_id?.toString()) && !isNil(field.properties.form)) {
        const subformId = SubformId.from(field.properties.form.uid);

        const convertedSubformFields = (field.properties.form.fields ?? []).map(
          toFieldConfig(addMissingProperties, [], form, subformId),
        );

        const subform: Subform = {
          uid: subformId,
          meta: {
            name: field.properties.label_text,
          },
          emailConfiguration: toSubformEmailConfiguration(field.properties.form, convertedSubformFields, []),
        };

        convertedRules.push(
          ...field.properties.form.rules.map((ruleDto) =>
            toRule(ruleDto, toFieldMap(convertedSubformFields), new Map<string, VariableConfig>(), subform.uid),
          ),
        );
        const result = convertToLinkedSubforms(convertedSubformFields, addMissingProperties, form);
        convertedSubforms.push(subform, ...result.convertedSubforms);
        convertedFields.push(...convertedSubformFields, ...result.convertedFields);
        convertedRules.push(...result.convertedRules);

        field.properties.target_form_id = subform.uid;
      }

      delete field.properties.form;
    }

    if (isPinField(field)) {
      field.properties.pins.forEach((pin) => {
        if (isEmpty(pin.target_form_id?.toString()) && !isNil(pin.form)) {
          const subformId = SubformId.from(pin.form.uid);

          const convertedSubformFields = pin.form.fields.map(toFieldConfig(addMissingProperties, [], form, subformId));

          const subform: Subform = {
            uid: subformId,
            meta: {
              name: pin.name,
            },
            emailConfiguration: toSubformEmailConfiguration(pin.form, convertedSubformFields, []),
          };

          const fieldMap = toFieldMap(convertedSubformFields);
          convertedRules.push(
            ...pin.form.rules.map((ruleDto) =>
              toRule(ruleDto, fieldMap, new Map<string, VariableConfig>(), subform.uid),
            ),
          );
          const result = convertToLinkedSubforms(convertedSubformFields, addMissingProperties, form);
          convertedSubforms.push(subform, ...result.convertedSubforms);
          convertedFields.push(...convertedSubformFields, ...result.convertedFields);
          convertedRules.push(...result.convertedRules);

          pin.target_form_id = SubformId.from(pin.form.uid);
        }

        delete pin.form;
      });
    }
  });

  return { convertedSubforms, convertedFields, convertedRules };
};

const replaceIdsWithValueReferences = (
  fieldType: BaseField<unknown, unknown>,
  properties: Record<string, unknown>,
  variables: VariableConfig[],
) => {
  const keysToReplace = getKeys(fieldType.schema);
  return Object.entries(properties).reduce(
    (acc, [key, value]) => {
      if (!keysToReplace.includes(key)) {
        return { ...acc, [key]: value };
      }

      if (isString(value)) {
        return { ...acc, [key]: createReference(value, variables) };
      } else if (isArray(value)) {
        return { ...acc, [key]: value.map((v) => (isString(v) ? createReference(v, variables) : v)) };
      } else {
        return { ...acc, [key]: value };
      }
    },
    {} as Record<string, unknown>,
  );
};

const createReference = (value: string, variables: VariableConfig[]) =>
  variables.some((variable) => variable.uid.equals(value))
    ? VariableValueReference.from(value)
    : FieldValueReference.from(value);

const getKeys = (schema: GenericSchema<unknown, unknown, BaseIssue<unknown>>): string[] => {
  switch (schema.type) {
    case "object":
      return getObjectKeys(schema as ObjectSchema<ObjectEntries, never>);
    case "intersect":
      return getIntersectKeys(schema as IntersectSchema<IntersectOptions, never>);
    case "union":
      return getUnionKeys(schema as UnionSchema<UnionOptions, never>);
  }
  return [];
};

const getObjectKeys = (schema: ObjectSchema<ObjectEntries, never>): string[] =>
  Object.entries(schema.entries)
    .filter(([, value]) => {
      if (value.type === "array") {
        return isReference(
          (value as ArraySchema<GenericSchema<unknown, unknown, BaseIssue<unknown>>, never>).item.expects,
        );
      } else if (value.type === "non_nullish" && "wrapped" in value) {
        // @ts-expect-error No type exposed by ValiBot
        return isReference(value.wrapped.expects);
      } else {
        return isReference(value.expects);
      }
    })
    .map(([key]) => key);

const getIntersectKeys = (schema: IntersectSchema<IntersectOptions, never>): string[] =>
  Object.entries(schema.options).flatMap(([, schema]) => getKeys(schema));

const getUnionKeys = (schema: UnionSchema<UnionOptions, never>) => schema.options.flatMap((option) => getKeys(option));

const fieldIdToDataName = (fieldId: FieldId, fields: FieldConfig[]): string | undefined => {
  const field = fields.find((field) => field.uid.equals(fieldId));

  if (field !== undefined) {
    return field.properties.data_name as string;
  }
};

const removeUndefined = <T>(item: T): item is Exclude<T, undefined> => item !== undefined;

const isReference = (expects: string) =>
  expects === ValueReference.name ||
  expects.includes(FieldValueReference.name) ||
  expects.includes(VariableValueReference.name);
